import { FC, useEffect, useState } from 'react';
import {
  Column,
  ColumnDef,
  FilterFn,
  flexRender,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { BreadcrumbItemType, ExplorerItem } from '@/types/file-explorer-types';

import { cn } from '@/lib/utils';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useGlobalStore } from '@/stores/globalStore';

import {
  ExplorerItemType,
  getColumnsConfig,
} from '@/constants/table-columns-config';

import { Skeleton } from '../ui/skeleton';
import BreadCrumbsComponent from './BreadCrumbsComponent';
import SearchComponentAutosuggest from './SearchComponentAutosuggest';
import { ColumnHeaderDefault, getSmallColumns } from './TableColumns';

declare module '@tanstack/table-core' {
  interface FilterFns {
    exact: FilterFn<any>;
  }
}

const TableRowSkeleton = ({ columnCount }: { columnCount: number }) => (
  <>
    {Array(8)
      .fill(0)
      .map((_, rowIndex) => (
        <tr key={rowIndex} className="animate-pulse border-b border-gray-200">
          {Array(columnCount)
            .fill(0)
            .map((_, colIndex) => (
              <td
                key={colIndex}
                className="h-[60px] border-b border-separator px-[20px] py-3 sm:h-[70px] sm:px-4"
              >
                <Skeleton className="h-6 w-full bg-gray-100" />
              </td>
            ))}
        </tr>
      ))}
  </>
);

interface BasicViewProps {
  items: ExplorerItem[];
  breadcrumbs: BreadcrumbItemType[];
  entityType: ExplorerItemType;
  setCurrentUUID: (uuid: string) => void;
  setCurrentEntityType: (type: ExplorerItemType) => void;
  onNavigate?: (params: { uuid: string; entityType: ExplorerItemType }) => void;
  isSmall?: boolean;
  onImageClick?: (uuid: string) => void;
  searchTerm?: string;
  isLoading?: boolean;
  isMoveFile?: boolean;
  refetchData?: (pagination?: { pageIndex: number; pageSize: number }) => void;
  totalCount: number; // Total number of items for pagination
  rowsPerPage: number;
}

export const BasicTableView: FC<BasicViewProps> = ({
  items,
  breadcrumbs,
  entityType,
  setCurrentUUID,
  onImageClick,
  setCurrentEntityType,
  onNavigate,
  isSmall = false,
  searchTerm,
  isLoading = false,
  isMoveFile,
  refetchData,
  totalCount,
  rowsPerPage,
}) => {
  const { updateFileAfterMove } = useApiManagerStore();
  const { fileUuidToMove, setFileUuidToMove, setIsMoveFileDialogOpen } =
    useGlobalStore();

  const [sorting, setSorting] = useState<SortingState>([]);

  const [pagination, setPagination] = useState(() => ({
    pageIndex: 0,
    pageSize: rowsPerPage,
  }));

  useEffect(() => {
    setPagination(() => ({
      pageIndex: 0,
      pageSize: rowsPerPage,
    }));
  }, [rowsPerPage, entityType]);

  useEffect(() => {
    refetchData?.(pagination);
  }, [pagination.pageIndex, pagination.pageSize]);

  // Event handlers
  const handleNavigate = (uuid: string, type: ExplorerItemType) => {
    onNavigate?.({
      uuid,
      entityType: type,
    });
  };

  const handlePreviousPage = () => {
    if (pagination.pageIndex > 0) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: prev.pageIndex - 1,
      }));
    }
  };

  const handleNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: prev.pageIndex + 1,
    }));
  };

  const canPreviousPage = pagination.pageIndex > 0;
  const canNextPage = items.length === rowsPerPage;

  const getRegularColumns = (): ColumnDef<ExplorerItem>[] => {
    return getColumnsConfig(entityType).map((column) => ({
      ...column,
      header: ({ column: tableColumn }: { column: Column<ExplorerItem> }) => (
        <ColumnHeaderDefault column={tableColumn} entityType={entityType} />
      ),
    })) as ColumnDef<ExplorerItem>[];
  };

  const columns = isSmall
    ? getSmallColumns(
        entityType,
        false,
        fileUuidToMove,
        updateFileAfterMove,
        setFileUuidToMove,
        setIsMoveFileDialogOpen,
        setCurrentUUID,
        setCurrentEntityType,
        handleNavigate,
      )
    : getRegularColumns();

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    filterFns: {
      exact: () => true,
    },
    autoResetAll: false,
  });

  return (
    <>
      <div
        className={cn(
          `relative flex flex-1 flex-col gap-1 rounded-md bg-white transition-all duration-300 ease-in-out`,
          isSmall ? '' : 'overflow-hidden shadow-sm',
        )}
      >
        {isSmall && (
          <SearchComponentAutosuggest
            setCurrentUUID={setCurrentUUID}
            setCurrentEntityType={setCurrentEntityType}
            onNavigate={onNavigate}
          />
        )}
        {!isSmall && (
          <div className="absolute right-6 top-6 z-10 flex items-center justify-end gap-2">
            <div className="flex items-center justify-center px-3 py-1.5">
              <div className="relative flex h-[22px] w-[22px] items-center justify-center rounded-[4px] border border-primary sm:h-[26px] sm:w-[26px]">
                <button
                  onClick={handlePreviousPage}
                  disabled={isLoading || !canPreviousPage}
                  className={`absolute right-full top-1/2 -translate-y-1/2 transition-opacity ${isLoading ? 'opacity-50' : ''}`}
                >
                  <ChevronLeft className="h-[14px] w-[14px] sm:h-4 sm:w-4" />
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={isLoading || !canNextPage}
                  className={`absolute left-full top-1/2 -translate-y-1/2 transition-opacity ${isLoading ? 'opacity-50' : ''}`}
                >
                  <ChevronRight className="h-[14px] w-[14px] sm:h-4 sm:w-4" />
                </button>
                <span>{pagination.pageIndex + 1}</span>
              </div>
            </div>
          </div>
        )}
        <BreadCrumbsComponent
          breadcrumbs={breadcrumbs}
          isSmall={isSmall}
          searchTerm={searchTerm}
          setCurrentUUID={setCurrentUUID}
          setCurrentEntityType={setCurrentEntityType}
          handleNavigate={handleNavigate}
          totalDocuments={totalCount}
          isRootLevel={false}
        />
        <div className="flex-grow overflow-auto">
          <div className={cn('flex-1 rounded-md transition-all duration-300')}>
            <div className="overflow-x-auto">
              <table
                className={cn(
                  'w-full border-separate border-spacing-0 sm:min-w-[640px]',
                  isSmall && 'sm:min-w-[340px]',
                )}
              >
                {!isSmall && (
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={`w-auto border-b px-[20px] text-left font-medium sm:px-4 ${
                              isSmall
                                ? 'py-1 text-sm sm:first:pl-[32px] sm:last:pr-[32px]'
                                : 'py-5 text-base sm:first:pl-[36px] sm:last:pr-[60px]'
                            }`}
                            style={{
                              width:
                                header.column.id === 'name' &&
                                columns.length < 4
                                  ? '100%'
                                  : 'auto',
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                )}
                <tbody>
                  {isLoading ? (
                    <TableRowSkeleton columnCount={columns.length} />
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className={`cursor-pointer border-b border-gray-200 ${
                          entityType === ExplorerItemType.DIRECTORY
                            ? ' hover:bg-neutral-50'
                            : ''
                        }`}
                        onClick={() => {
                          if (
                            isMoveFile &&
                            entityType === ExplorerItemType.AGREEMENT
                          ) {
                            return;
                          }
                          if (entityType === ExplorerItemType.FILE) {
                            onImageClick?.(row.original.uuid);
                            return;
                          }
                          const newEntityType = entityType + 1;
                          setCurrentUUID(row.original.uuid);
                          setCurrentEntityType(newEntityType);
                          handleNavigate(row.original.uuid, newEntityType);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`border-b border-separator px-[20px] py-3 sm:px-4 ${
                              isSmall
                                ? 'h-[60px] sm:first:pl-[32px] sm:last:pr-[32px]'
                                : 'h-[60px] sm:h-[70px] sm:first:pl-[36px] sm:last:pr-[60px]'
                            }`}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
